import React, { useState, useRef, useCallback } from 'react';
import { Container, Alert } from 'react-bootstrap';
import { useSkinSeoulRematching } from '../../../apps/hooks/sourcing/useSkinSeoulRematching';
import { SkinSeoulProductsTable } from './components/SkinSeoulProductsTable';
import { SSCpRematchingCoupangTable } from './components/SSCpRematchingCoupangTable';
import { SkinSeoulProductType } from '../../../models/ProductTypes';

const SSCPRematchingPage = () => {
  const {
    // SkinSeoul products data
    products,
    totalProducts,
    selectedProduct,
    selectedVariation,
    filters,
    pagination,
    skuInput,
    isLoading,
    error,

    // SkinSeoul products actions
    handleProductSelect,
    handleVariationSelect,
    handlePageChange,
    handlePageSizeChange,
    updateFilters,
    handleSkuInputChange,
    clearSkuSearch,

    // Coupang data and actions
    coupangProducts,
    coupangLoading,
  } = useSkinSeoulRematching();

  // Toast state for notifications
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  // Global filter state for fast delivery (3 days or less)
  const [showFastDeliveryOnly, setShowFastDeliveryOnly] = useState(true);

  // Helper function for showing toast messages
  const showToastMessage = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
    }, 2000);
  };

  // Resizable layout state (similar to existing matching page)
  const [leftWidth, setLeftWidth] = useState(() => {
    // Default to 40% of screen width to accommodate more product information
    return 40;
  });
  const [isResizing, setIsResizing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Handle mouse events for resizing
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsResizing(true);
    e.preventDefault();
  };

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing || !containerRef.current) {
        return;
      }

      const container = containerRef.current;
      const containerRect = container.getBoundingClientRect();
      const relativeX = e.clientX - containerRect.left;
      const newLeftWidth = Math.round((relativeX / containerRect.width) * 100);

      // Limit width between 30% and 70%
      const clampedWidth = Math.max(30, Math.min(70, newLeftWidth));
      setLeftWidth(clampedWidth);
    },
    [isResizing],
  );

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  // Add/remove mouse event listeners
  React.useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  return (
    <Container fluid className="py-1" style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div className="mb-2">
        <span className="h5 mb-1">SS CP Re-Matching</span>
        <p className="text-muted mb-0" style={{ fontSize: '0.85rem', lineHeight: '1.3' }}>
          <strong>How to Re-Match:</strong> Select an unpublished SkinSeoul product (left) → Review Coupang search
          results (right) → Copy Coupang URL and append data to Google Sheets for manual processing
        </p>
      </div>

      {error && (
        <Alert variant="danger" dismissible>
          <strong>Error:</strong> {error.message || 'Failed to load products'}
        </Alert>
      )}

      {/* Resizable Layout Container */}
      <div
        ref={containerRef}
        className="flex-grow-1"
        style={{
          height: 'calc(100% - 100px)',
          display: 'flex',
          position: 'relative',
        }}
      >
        {/* SkinSeoul Products Panel */}
        <div
          style={{
            width: `${leftWidth}%`,
            minWidth: '400px',
            paddingRight: '8px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <SkinSeoulProductsTable
            products={products}
            selectedProduct={selectedProduct}
            selectedVariation={selectedVariation}
            onProductSelect={handleProductSelect}
            onVariationSelect={handleVariationSelect}
            loading={isLoading}
            totalProducts={totalProducts}
            currentPage={pagination.page}
            pageSize={pagination.limit}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            postStatus={filters.postStatus}
            onPostStatusChange={(status) => updateFilters({ postStatus: status })}
            productType={filters.productType}
            onProductTypeChange={(type) => updateFilters({ productType: type })}
            stockStatus={filters.stockStatus}
            onStockStatusChange={(status) => updateFilters({ stockStatus: status })}
            coupangProducts={coupangProducts}
            skuSearch={skuInput}
            onSkuSearchChange={handleSkuInputChange}
            onSkuSearchClear={clearSkuSearch}
            activeSkuFilter={filters.sku}
          />
        </div>

        {/* Resizer Handle */}
        <div
          className="resizer"
          onMouseDown={handleMouseDown}
          style={{
            width: '4px',
            cursor: 'col-resize',
            backgroundColor: isResizing ? '#007bff' : '#dee2e6',
            position: 'relative',
            transition: isResizing ? 'none' : 'background-color 0.2s',
          }}
        />

        {/* Coupang Products Panel */}
        <div
          style={{
            width: `${100 - leftWidth}%`,
            minWidth: '400px',
            paddingLeft: '8px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {selectedProduct ? (
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <div style={{ flex: 1, overflow: 'auto' }}>
                {(() => {
                  // Check for Variable products FIRST (before checking results)
                  // Only show Variable message if no variation is selected
                  if (selectedProduct && selectedProduct.type === SkinSeoulProductType.Variable && !selectedVariation) {
                    return (
                      <div className="d-flex align-items-center justify-content-center h-100">
                        <div className="text-center text-muted">
                          <i className="fas fa-sitemap fa-3x mb-3 opacity-50"></i>
                          <h5>Variable product is selected</h5>
                          <p>
                            Variable products are not supported for Coupang search. <br />
                            Click on a variation to see Coupang search results
                          </p>
                        </div>
                      </div>
                    );
                  } else if (coupangProducts.length > 0) {
                    return (
                      <SSCpRematchingCoupangTable
                        selectedSkinSeoulProduct={selectedVariation || selectedProduct}
                        cpSearchList={coupangProducts}
                        loading={coupangLoading}
                        onShowToast={showToastMessage}
                        showFastDeliveryOnly={showFastDeliveryOnly}
                        setShowFastDeliveryOnly={setShowFastDeliveryOnly}
                      />
                    );
                  } else if (coupangLoading) {
                    return (
                      <div className="d-flex align-items-center justify-content-center h-100">
                        <div className="text-center text-muted">
                          <div className="spinner-border mb-3" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <h5>Searching Coupang products...</h5>
                          <p>Please wait while we fetch matching products</p>
                        </div>
                      </div>
                    );
                  } else {
                    return (
                      <div className="d-flex align-items-center justify-content-center h-100">
                        <div className="text-center text-muted">
                          <i className="fas fa-search-minus fa-3x mb-3 opacity-50"></i>
                          <h5>No matched products found</h5>
                          <p>No matching Coupang products were found for this SkinSeoul product</p>
                        </div>
                      </div>
                    );
                  }
                })()}
              </div>
            </div>
          ) : (
            <div className="d-flex align-items-center justify-content-center h-100 text-muted">
              <div className="text-center">
                <div className="mb-3">
                  <i className="fas fa-hand-pointer fa-3x"></i>
                </div>
                <h5>Select a SkinSeoul Product</h5>
                <p>Choose a product from the left panel to see Coupang search results</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Toast for notifications */}
      {showToast && (
        <div className="position-fixed bottom-0 end-0 p-3" style={{ zIndex: 1050 }}>
          <div className="toast show" role="alert">
            <div className="toast-body">{toastMessage}</div>
          </div>
        </div>
      )}
    </Container>
  );
};

export default SSCPRematchingPage;
